import { HttpErrorResponse } from '@angular/common/http';
import { isIUpdateVarErrorsResult } from './results/result';

/**
 * A custom error class that extends HttpErrorResponse to include a specific error type.
 * @see {@link https://github.com/angular/angular/issues/45336}
 */
export class IHttpErrorResponse<T> extends HttpErrorResponse {
  override error: T | null = null;
}

/**
 * Extracts a user-friendly message from various error response formats.
 * @param errorVal The error value which can be of various types.
 * @returns A string message if available, otherwise undefined.
 */
export function getMessageFromError(errorVal: unknown): string | undefined {
  if (errorVal === null || errorVal === undefined) return undefined;
  if (typeof errorVal === 'string') return errorVal;
  if (isIAdvancedError(errorVal) && errorVal.error.message) {
    return errorVal.error.message;
  } else if (isIErrorResult(errorVal)) {
    // If the error is an IErrorResult, concatenate the text of all results into a single message.
    return combineTextsForError(errorVal.results);
  } else if (isIUpdateVarErrorsResult(errorVal) && errorVal.errors?.length > 0) {
    // If the error is an IUpdateVarErrorsResult with validation errors, concatenate them into a single message.
    return combineTextsForError(errorVal.errors);
  } else if (isIExceptionError(errorVal)) {
    // ASP.NET Web API errors carry the useful detail in 'exceptionMessage' while 'message' is a generic placeholder.
    return errorVal.exceptionMessage;
  } else if (isIMessageError(errorVal)) {
    // If the error has a 'message' property, return it.
    return errorVal.message;
  } else if (isIErrorError(errorVal)) {
    // If the error has an 'error' property, return it.
    return errorVal.error;
  }
  return undefined;
}

/**
 * Combines multiple error texts into a single formatted string.
 * @param errTexts An array of objects that may contain a 'text' property with error messages.
 * @returns A single string combining all error messages, separated by line breaks.
 */
function combineTextsForError(errTexts: { text?: string }[]): string {
  return errTexts
    .filter((e) => e.text && e.text.trim() !== '')
    .map((e) => `- ${e.text}`)
    .join('<br/>');
}

/**
 * Tries to extract a title from the error value, if it exists.
 * @param errorVal The error value which can be of various types.
 * @returns A string title if extractable, otherwise undefined.
 */
export function getTitleFromError(errorVal: unknown): string | undefined {
  if (errorVal === null || errorVal === undefined) return undefined;
  if (isIAdvancedError(errorVal) && errorVal.error.title) {
    return errorVal.error.title;
  }
  return undefined;
}

/** Represents an error with a message. */
interface IMessageError {
  message: string;
}
/** Type guard to check if an error is an IMessageError. */
export function isIMessageError(error: unknown): error is IMessageError {
  return typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string';
}

/** Represents an ASP.NET Web API error carrying a detailed exception message. */
interface IExceptionError {
  /** Detailed exception message returned by the API. */
  exceptionMessage: string;
}
/** Type guard to check if an error is an IExceptionError. */
export function isIExceptionError(error: unknown): error is IExceptionError {
  return typeof error === 'object' && error !== null && 'exceptionMessage' in error && typeof error.exceptionMessage === 'string';
}

/** Represents an error with an error string. */
interface IErrorError {
  error: string;
}
/** Type guard to check if an error is an IErrorError. */
export function isIErrorError(error: unknown): error is IErrorError {
  return typeof error === 'object' && error !== null && 'error' in error && typeof error.error === 'string';
}

/** Represents an advanced error structure with nested title and message fields. */
interface IAdvancedError {
  error: {
    title?: string;
    message?: string;
  };
}
/** Type guard to check if an error is an IAdvancedError. */
export function isIAdvancedError(error: unknown): error is IAdvancedError {
  // Is the object non-null and has an 'error' property that is also a non-null object?
  if (typeof error !== 'object' || error == null || !('error' in error)) return false;
  // Is the 'error' property itself an object that is non-null?
  if (typeof error.error !== 'object' || error.error == null) return false;
  // Does the 'error' object have a 'title' or 'message' property that is a string?
  const hasValidTitle = 'title' in error.error && typeof error.error.title === 'string';
  const hasValidMessage = 'message' in error.error && typeof error.error.message === 'string';
  return hasValidTitle || hasValidMessage;
}

/** Represents an error result containing a results array with a result type and optional text. */
interface IErrorResult {
  results: Array<{
    resultType: string;
    text?: string;
  }>;
}
/** Type guard to check if an error is an IErrorResult. */
export function isIErrorResult(error: unknown): error is IErrorResult {
  // Is the object non-null and has a 'results' property that is also a non-null array?
  if (typeof error !== 'object' || error == null || !('results' in error)) return false;
  // Is the 'results' property itself an array that is non-null?
  if (!Array.isArray(error.results) || error.results == null) return false;
  // Does the 'results' array have at least one element with a 'resultType' property that is a string?
  if (error.results.length === 0) return false;
  return error.results.every((r) => typeof r.resultType === 'string');
}
